import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, MapPin, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Server-proxied Google Places (New) address autocomplete. Used across the
 * app anywhere the operator types a property or business address.
 *
 * Props:
 *   - `name` — input name attribute (so it stays form-submittable)
 *   - `placeholder`
 *   - `onSelect(label)` — fired when the user clicks/Enter-keys a prediction
 *   - `onParsed({street, city, state, zip, label})` — same event, but with
 *     the address decomposed into components so the parent can auto-fill
 *     related fields (city/state/zip inputs).
 *   - `inputClassName`, `dropdownClassName` — style overrides
 *   - `dataTestid` — applied to the input
 *   - `cta` — optional element rendered absolutely on the input's right edge
 *     (e.g. the "Analyze it" submit button). Receives no props; the parent
 *     stays in control of submit behavior.
 *   - `value` / `onChange` — optional controlled mode
 */
export function AddressAutocomplete({
  name = "address",
  placeholder = "Enter a property address…",
  onSelect,
  onParsed,
  inputClassName = "",
  dropdownClassName = "",
  dataTestid,
  cta = null,
  // Optional controlled mode — when `value` is provided the parent owns the
  // input; we fire `onChange(label)` on every keystroke and `onSelect(label)`
  // when a prediction is picked. When omitted, the component manages its own
  // internal state (current landing-hero behavior).
  value: controlledValue,
  onChange: controlledOnChange,
}) {
  const isControlled = controlledValue !== undefined;
  const [innerValue, setInnerValue] = useState("");
  const value = isControlled ? controlledValue : innerValue;
  const setValue = (v) => {
    if (isControlled) controlledOnChange?.(v);
    else setInnerValue(v);
  };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const debRef = useRef(null);
  const reqIdRef = useRef(0);

  // Debounced fetch
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!value || value.trim().length < 3) {
      setItems([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/places/autocomplete`, {
          params: { q: value },
          timeout: 7000,
        });
        if (reqIdRef.current !== myReq) return;  // ignore stale response
        setItems(data?.predictions || []);
        setOpen(true);
        setActive(-1);
      } catch {
        setItems([]);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    }, 200);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [value]);

  // Click outside closes
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = async (item) => {
    setItems([]);
    setOpen(false);
    setActive(-1);
    onSelect?.(item.label);
    if (!onParsed) {
      // Simple mode: no decomposition needed, just echo the label back.
      setValue(item.label);
      return;
    }
    // Decomposition mode: prefer Google's Place Details (guaranteed ZIP +
    // structured components). Fall back to parsing the label text if the
    // details call fails so we still surface *something* to the parent form.
    // IMPORTANT: skip setValue here — onParsed owns the parent's address
    // field. Calling both would race and stale-closure overwrite the
    // city/state/zip we just set.
    try {
      const { data } = await axios.get(
        `${API}/places/details?place_id=${encodeURIComponent(item.place_id)}`,
      );
      const hasAny = data && (data.street || data.city || data.state || data.zip);
      if (hasAny) {
        onParsed({ ...data, label: data.label || item.label });
        return;
      }
    } catch (_) { /* fall through */ }
    onParsed({ ...parseUsAddress(item.label), label: item.label });
  };
  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      choose(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--ink-3)] pointer-events-none" />
      {loading && (
        <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--ink-3)] pointer-events-none" style={{ right: cta ? "11rem" : "0.75rem" }} />
      )}
      <input
        name={name}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => items.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        data-testid={dataTestid}
        aria-autocomplete="list"
        aria-controls="address-autocomplete-listbox"
        aria-expanded={open}
      />
      {cta}
      {open && items.length > 0 && (
        <ul
          id="address-autocomplete-listbox"
          role="listbox"
          className={`absolute left-0 right-0 top-full mt-1 z-50 rounded-xl bg-white border border-[var(--line)] shadow-lg overflow-hidden ${dropdownClassName}`}
          data-testid="address-autocomplete-dropdown"
        >
          {items.map((it, i) => (
            <li
              key={it.place_id}
              role="option"
              aria-selected={active === i}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(it); }}
              className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer text-[var(--ink)] ${active === i ? "bg-[var(--bg-2)]" : "hover:bg-[var(--bg-2)]"}`}
              data-testid={`address-prediction-${i}`}
            >
              <MapPin size={14} className="text-[var(--ink-3)] shrink-0" />
              <span className="truncate text-sm">{it.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Parse a Google-formatted US address label into components. Handles the
 * canonical shape "123 Main St, Philadelphia, PA 19107, USA" and its many
 * cousins. Anything unparseable falls through as empty strings — never
 * throws.
 *
 * Exported so tests + form components can call it directly (e.g. to re-parse
 * a stored address, or handle a stale free-text field).
 */
export function parseUsAddress(label) {
  const empty = { street: "", city: "", state: "", zip: "" };
  if (!label || typeof label !== "string") return empty;
  // Drop trailing ", USA" / ", United States"
  const cleaned = label.replace(/,\s*(USA|United States)\.?\s*$/i, "").trim();
  const parts = cleaned.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return { ...empty, street: cleaned };
  // Last chunk usually holds "STATE ZIP" (e.g. "PA 19107"). Fall back to
  // second-to-last if the string was oddly shaped.
  const stateZipRe = /^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/;
  let street = "", city = "", state = "", zip = "";
  const last = parts[parts.length - 1];
  const m = last.match(stateZipRe);
  if (m) {
    state = m[1];
    zip = m[2];
    city = parts[parts.length - 2] || "";
    street = parts.slice(0, parts.length - 2).join(", ");
  } else {
    // Try to salvage — sometimes Google returns "PA" and "19107" separately.
    const stateOnly = last.match(/^([A-Z]{2})$/);
    if (stateOnly && parts.length >= 3) {
      state = stateOnly[1];
      city = parts[parts.length - 2] || "";
      street = parts.slice(0, parts.length - 2).join(", ");
    } else {
      // Give up gracefully — dump everything into street so nothing is lost.
      street = cleaned;
    }
  }
  return { street, city, state, zip };
}
